import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { InfoBlockProps } from '../types';
import { useTelegramWebApp } from '../../../hooks/useTelegramWebApp';
import { CopyIcon, CheckIcon, InfoIcon, ChevronDownIcon } from '../icons';
import styles from './InfoBlock.module.css';

/**
 * InfoBlock - Card component for displaying labeled information
 * 
 * Features:
 * - Copy to clipboard with visual feedback
 * - Collapsible content with fade gradient
 * - Smooth animations with dynamic height measurement
 */
export const InfoBlock: React.FC<InfoBlockProps> = ({
  label,
  value,
  canCopy = false,
  collapsible = false,
  className,
}) => {
  const { t } = useTranslation('image');
  const { haptic } = useTelegramWebApp();
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!collapsible);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  // Measure actual content height for smooth animation
  useEffect(() => {
    if (!collapsible || !contentRef.current) return;
    const measure = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
    };
    measure();
    // Re-measure on window resize
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value, collapsible]);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    haptic.impact('light');
    navigator.clipboard.writeText(value);
    setCopied(true);
  }, [value, haptic]);

  // Reset copy state after animation
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  // Highlight @mentions in the value text
  const highlightedValue = useMemo(() => {
    if (!value || !value.includes('@')) return null;
    const parts = value.split(/(@\S+)/g);
    if (parts.length <= 1) return null;
    return parts.map((part, i) => {
      if (part.startsWith('@') && part.length > 1) {
        return <span key={i} className={styles.mentionTag}>{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }, [value]);

  return (
    <div className={clsx(styles.card, className)}>
      {/* Header inside card — info icon + label, with copy on the right */}
      <div className={styles.header}>
        <span className={styles.headerIcon}><InfoIcon /></span>
        <span className={styles.headerLabel}>{label}</span>

        {canCopy && (
          <div className={styles.copyContainer}>
            <div className={clsx(styles.copySuccess, copied && styles.visible)}>
              {t('gallery.promptCopied')}
            </div>
            <button
              className={clsx(styles.copyButton, copied && styles.success)}
              onClick={handleCopy}
              title="Copy"
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
          </div>
        )}
      </div>

      {/* Body — prompt value */}
      <div className={styles.body}>
        <div
          ref={contentRef}
          className={clsx(styles.content, isExpanded ? styles.expanded : styles.collapsed)}
          style={isExpanded && contentHeight ? { maxHeight: contentHeight + 16 } : undefined}
        >
          <div className={clsx(styles.value, copied && styles.copied)}>
            {highlightedValue || value}
          </div>

          {collapsible && !isExpanded && (
            <div className={styles.fadeGradient} />
          )}
        </div>
      </div>

      {/* See-all toggle (same style as ParametersBlock) */}
      {collapsible && (
        <button className={styles.seeAllToggle} onClick={handleToggle}>
          <span>{isExpanded ? t('gallery.hide') : t('gallery.show')}</span>
          <span className={clsx(styles.seeAllChevron, isExpanded && styles.rotated)}>
            <ChevronDownIcon />
          </span>
        </button>
      )}
    </div>
  );
};

export default InfoBlock;
